import * as Model from "@effect/sql/Model";
import { Schema } from "effect";

/**
 * What a run *is*.
 *
 * - `orchestrator`: the loop that decides what to spawn next
 * - `agent`: a general agent execution
 * - `task`: a narrowly scoped unit of work
 * - `verifier`: checks a prior run's work and emits a verdict
 * - `human`: not an execution at all — a wait for a person to answer
 * - `outcome`: not an execution either — a wait for a CHILD outcome. The run
 *   row is the parent's handle on the nested outcome: `childOutcomeId` names
 *   it, and the child's terminal status resolves this run's deferred (OL-66).
 * - `build`: builds a repo's `Dockerfile.orca` into a delta image and gates it.
 *   The runner clones the repo at an immutable ref, builds the delta image FROM
 *   the harness base, runs the repo's declared contract gates INSIDE it with the
 *   network off, and pushes only if every gate exits 0 (OL-71).
 *
 * **Deploy ordering for `build`.** The literal is consumed by
 * `@fm/cluster`'s `orcaloops-effects.rpc.ts` and decoded by `extractDecision`
 * (`Schema.decodeUnknownEither(RunDecision)`), so an orchestrator emitting
 * `kind: "build"` before BOTH the api and the cluster ship this widened literal
 * makes the ENTIRE decision block fail to parse. The widened literal must
 * therefore be deployed in both the api and the cluster BEFORE anything creates
 * a run of that kind. Nothing in OL-71 emits it; the orchestrator half (OL-92)
 * owns that sequencing.
 *
 * `runs.kind` is a plain `varchar` with this TS-side union — no pgEnum, no CHECK
 * constraint — so adding a literal needs no migration and the enum-value
 * isolation rule (M6) does not apply.
 *
 * @since 1.0.0
 */
export const RunKind = Schema.Literal(
  "orchestrator",
  "agent",
  "task",
  "verifier",
  "human",
  "outcome",
  "build",
);
export type RunKind = Schema.Schema.Type<typeof RunKind>;

/**
 * Lifecycle of a run. `waiting` is the human-wait state; `timed_out` is
 * distinct from `failed` so a deadline miss is not confused with an error.
 *
 * @since 1.0.0
 */
export const RunStatus = Schema.Literal("pending", "running", "waiting", "done", "failed", "canceled", "timed_out");
export type RunStatus = Schema.Schema.Type<typeof RunStatus>;

/**
 * What the orchestrator decided to do at the end of a turn.
 *
 * @since 1.0.0
 */
export const RunDecisionAction = Schema.Literal("spawn", "ask", "done", "fail");
export type RunDecisionAction = Schema.Schema.Type<typeof RunDecisionAction>;

/**
 * One child run the orchestrator asked for in a `spawn` decision.
 *
 * @since 1.0.0
 */
export const RunDecisionSpawn = Schema.Struct({
  kind: RunKind,
  title: Schema.String,
  brief: Schema.String,
  acceptance: Schema.optional(Schema.Array(Schema.String)),
  model: Schema.optional(Schema.String),
  effort: Schema.optional(Schema.String),
  repo: Schema.optional(Schema.String),
  /**
   * USD ceiling the orchestrator PROPOSES for a `kind: "outcome"` child
   * (OL-66). Only ever a proposal: `effects.startOutcome` carves the real
   * budget server-side out of the parent's remaining money and clamps this
   * down to it, so an orchestrator cannot spend what its parent does not have.
   */
  budget: Schema.optional(Schema.Number),
  /**
   * How the spawned run is to be checked. `command` runs `command` and reads
   * its exit code; `agent` spawns a verifier run that writes a verdict. Absent
   * means the orchestrator asked for no verification at all.
   */
  verifier: Schema.optional(Schema.Struct({
    mode: Schema.Literal("command", "agent"),
    command: Schema.optional(Schema.String),
  })),
});
export type RunDecisionSpawn = Schema.Schema.Type<typeof RunDecisionSpawn>;

/**
 * The question an `ask` decision puts to a human.
 *
 * @since 1.0.0
 */
export const RunDecisionQuestion = Schema.Struct({
  to: Schema.String,
  text: Schema.String,
  options: Schema.optional(Schema.Array(Schema.String)),
  timeout_hours: Schema.optional(Schema.Number),
  /** The Linear issue the question is about, so the ask can link to its context. */
  linearIssueId: Schema.optional(Schema.String),
  /** The pull request the question is about, same purpose as `linearIssueId`. */
  pr: Schema.optional(Schema.Struct({
    repo: Schema.String,
    number: Schema.Number,
  })),
});
export type RunDecisionQuestion = Schema.Schema.Type<typeof RunDecisionQuestion>;

/**
 * The orchestrator's parsed decision block, persisted verbatim on the run that
 * produced it. `direction_check` is the orchestrator's own statement of whether
 * the outcome is still heading the right way — the cheapest drift detector we
 * have. Parsing lives in the run-report callback; this schema is storage only.
 *
 * @since 1.0.0
 */
export const RunDecision = Schema.Struct({
  action: RunDecisionAction,
  direction_check: Schema.String,
  runs: Schema.optional(Schema.Array(RunDecisionSpawn)),
  question: Schema.optional(RunDecisionQuestion),
  synthesis: Schema.optional(Schema.String),
});
export type RunDecision = Schema.Schema.Type<typeof RunDecision>;

/**
 * Run model — one agent execution (or one human wait) inside an outcome.
 *
 * `deferredToken` is the plaintext `@effect/workflow` DurableDeferred token
 * (deterministic, api-internal). `callbackTokenHash` is only ever the SHA-256
 * hex digest of the capability handed to the container — the plaintext is never
 * persisted, so this field can be read without leaking a replayable secret.
 *
 * `customerId` is a plain string with no foreign key; `customers` lives in the
 * `public` schema and this table lives in `orcaloops`.
 *
 * @since 1.0.0
 */
export class Run extends Model.Class<Run>("Run")({
  id: Model.Generated(Schema.String),

  customerId: Schema.String,
  outcomeId: Schema.String,
  parentRunId: Schema.NullOr(Schema.String),

  kind: RunKind,
  brief: Schema.String,
  status: RunStatus,

  runnerId: Schema.NullOr(Schema.String),
  containerName: Schema.NullOr(Schema.String),
  containerId: Schema.NullOr(Schema.String),

  deferredToken: Schema.NullOr(Schema.String),
  callbackTokenHash: Schema.NullOr(Schema.String),
  deferredName: Schema.NullOr(Schema.String),

  workflowExecutionId: Schema.NullOr(Schema.String),

  turn: Schema.NullOr(Schema.Number),

  decision: Schema.NullOr(RunDecision),

  model: Schema.NullOr(Schema.String),
  agentCli: Schema.NullOr(Schema.String),

  costUsd: Schema.Number,

  startedAt: Schema.NullOr(Schema.DateTimeUtc),
  endedAt: Schema.NullOr(Schema.DateTimeUtc),

  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,

  /** Human-readable label for the run, as the orchestrator named it. */
  title: Schema.NullOr(Schema.String),

  /**
   * What this run projected onto, for display and for the webhook's reverse
   * lookup. NOT a join key in either direction — the task→run link is
   * `orca_tasks.run_id` / `run_customer_id`.
   */
  linearIssueId: Schema.NullOr(Schema.String),
  prRepo: Schema.NullOr(Schema.String),
  prNumber: Schema.NullOr(Schema.Number),

  /**
   * The child outcome a `kind: "outcome"` run is waiting on (OL-66). Stamped by
   * `effects.startOutcome` in the SAME compare-and-set that moves the run
   * `pending → running`, so a non-null value is the durable proof the child was
   * already created and a retried call must not create a second one.
   */
  childOutcomeId: Schema.NullOr(Schema.String),

  /**
   * Which credential — and therefore WHOSE identity — this run acted as
   * (OL-63). `null` when the run used the env pool or the repo's GitHub App
   * installation. An id only: the secret lives encrypted in
   * `orcaloops.credentials` and is never carried on a run.
   */
  credentialId: Schema.NullOr(Schema.String),
}) {}
